import { useCallback, useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Box,
  Flex,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Transition,
  UnstyledButton,
} from "@mantine/core";
import {
  IconBook2,
  IconChevronRight,
  IconCircleDot,
  IconHelpCircle,
  IconLayoutGrid,
  IconMessageCircle,
  IconMinus,
  IconSend,
} from "@tabler/icons-react";
import trpc from "@/shared/utils/trpc";
import { getTrpcErrorCode, handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useCollaborationHelp } from "@/context/CollaborationHelpContext";
import {
  APP_USAGE_GUIDE_STEPS,
  appUsageGuideStepIndexForPath,
} from "@/shared/constants/appUsageGuide.const";
import { PortalUsageGuideModal } from "@/components/profile/PortalUsageGuideModal";
import { SarahHelpMarkdown } from "@/components/help/SarahHelpMarkdown";
import { useLocation } from "react-router-dom";
import { useMediaQuery } from "@mantine/hooks";

/** Public asset (same folder as site logos). Already circular; do not frame or re-crop. */
const HELP_BOT_AVATAR_SRC = "/sarah-ai-assistant.png";

const NAVY = "#04305c";
const NAVY_SOFT = "#0a4a85";
const SUGGESTION_ACCENTS = ["#16A34A", "#7C3AED", "#2563EB", "#EA580C"] as const;

const SARAH_FAB_SIZE = 68;
const SARAH_FAB_BOTTOM_DESKTOP = 16;
/** Collab mobile: clear Chat Affix. Guest: stay near corner (no collab chat FAB). */
const SARAH_FAB_BOTTOM_MOBILE_COLLAB = 76;
const SARAH_FAB_RIGHT = 16;

const GUEST_TEASER_PHRASES = ["Ask me about GTsetu", "Hi, I am Sarah. How may I help you?"] as const;
const COLLAB_TEASER_PHRASES = [
  "Need help with this workspace?",
  "Hi, I am Sarah. How may I help you?",
] as const;

/** Guest home chips (fallback if DB empty; keep labels in sync with guest seed). */
const GUEST_HOME_CHIPS = [
  { id: "what_is_gtsetu", labelEn: "What is GTsetu?" },
  { id: "b2b_collaboration", labelEn: "How does B2B collaboration work?" },
  { id: "how_to_onboard", labelEn: "How do I onboard my company?" },
  { id: "what_are_experts", labelEn: "What are Experts?" },
  { id: "what_are_corridors", labelEn: "What are Corridors?" },
] as const;

function isGuestHomePath(pathname: string): boolean {
  return pathname === "/" || pathname === "/discover";
}

const SARAH_DEVICE_STORAGE_KEY = "gt_sarah_device_id";

/** Stable browser device token for guest rate limiting (localStorage). */
function getSarahDeviceId(): string {
  try {
    const existing = localStorage.getItem(SARAH_DEVICE_STORAGE_KEY);
    if (existing && /^[a-zA-Z0-9_-]{8,64}$/.test(existing)) return existing;
    const created =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID().replace(/[^a-zA-Z0-9_-]/g, "")
        : `d_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
    const normalized = created.slice(0, 64);
    if (normalized.length >= 8) {
      localStorage.setItem(SARAH_DEVICE_STORAGE_KEY, normalized);
      return normalized;
    }
  } catch {
    // private mode / blocked storage
  }
  return "unknown";
}

function HelpBotAvatar({ size }: { size: number }) {
  return (
    <img
      src={HELP_BOT_AVATAR_SRC}
      alt=""
      width={size}
      height={size}
      draggable={false}
      style={{
        display: "block",
        width: size,
        height: size,
        objectFit: "cover",
        borderRadius: "50%",
        background: "transparent",
        border: "none",
        outline: "none",
        boxShadow: "none",
        flexShrink: 0,
        pointerEvents: "none",
      }}
    />
  );
}

/** Hand-drawn style teaser bubble above Sarah FAB. */
function SarahTeaserBubble({
  text,
  visible,
  onClick,
}: {
  text: string;
  visible: boolean;
  onClick: () => void;
}) {
  return (
    <Transition mounted={visible} transition="pop" duration={220} timingFunction="ease">
      {(styles) => (
        <UnstyledButton
          type="button"
          onClick={onClick}
          aria-label={text}
          style={{
            ...styles,
            position: "absolute",
            right: 0,
            bottom: SARAH_FAB_SIZE + 10,
            maxWidth: 190,
            minWidth: 120,
            padding: "10px 14px",
            background: "#FFFFFF",
            color: "#0F172A",
            border: "2px solid #1E293B",
            borderRadius: "16px 16px 4px 16px",
            boxShadow: "0 6px 16px rgba(15, 23, 42, 0.14)",
            textAlign: "left",
            lineHeight: 1.25,
            zIndex: 1,
          }}
        >
          <Text size="xs" fw={700} c="#0F172A" style={{ whiteSpace: "normal" }}>
            {text}
          </Text>
          {/* Pointer toward Sarah */}
          <Box
            aria-hidden
            style={{
              position: "absolute",
              right: 22,
              bottom: -8,
              width: 12,
              height: 12,
              background: "#FFFFFF",
              borderRight: "2px solid #1E293B",
              borderBottom: "2px solid #1E293B",
              transform: "rotate(45deg)",
            }}
          />
        </UnstyledButton>
      )}
    </Transition>
  );
}

type ChatLine = {
  id: string;
  role: "user" | "assistant";
  text: string;
  at: number;
};

type FooterTab = "chat" | "topics" | "guide" | "help";
type Chip = { id: string; labelEn: string; slug?: string };

/** Prefer real DB node ids; also accept seed slug-style ids so FAQ resolve can match by slug. */
function isFaqChipId(id: string): boolean {
  return Boolean(id && id.trim().length >= 4 && !/\s/.test(id));
}

function formatTime(ts: number): string {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

/**
 * Floating Sarah: guest Discover home onboarding + collaboration workspace help.
 */
export default function GtsetuHelpFloatingBot() {
  const { isWorkspaceRoute, resolved, workspaceTab } = useCollaborationHelp();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 47.99em)");

  const isGuestHome = isGuestHomePath(location.pathname);
  /** Collaboration Workspace: Sarah only on Overview (hidden on Assessment / Documents / Meetings). */
  const isCollab =
    isWorkspaceRoute && Boolean(resolved) && workspaceTab === "overview";
  const surface: "guest" | "collab" | null = isGuestHome ? "guest" : isCollab ? "collab" : null;

  const [open, setOpen] = useState(false);
  const [footerTab, setFooterTab] = useState<FooterTab>("chat");
  const [messages, setMessages] = useState<ChatLine[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [tourOpened, setTourOpened] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [teaserIndex, setTeaserIndex] = useState(0);
  const [teaserVisible, setTeaserVisible] = useState(true);
  const [rootChips, setRootChips] = useState<Chip[]>([]);
  const [branchChips, setBranchChips] = useState<Chip[] | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fabBottom =
    isMobile && surface === "collab" ? SARAH_FAB_BOTTOM_MOBILE_COLLAB : SARAH_FAB_BOTTOM_DESKTOP;
  const panelBottom = fabBottom + SARAH_FAB_SIZE + 12;
  const panelTopClearance = isMobile ? 68 : 16;
  const panelMaxHeight = `min(560px, calc(100dvh - ${panelBottom + panelTopClearance}px))`;

  const teaserPhrases =
    surface === "guest" ? GUEST_TEASER_PHRASES : surface === "collab" ? COLLAB_TEASER_PHRASES : [];
  const teaserText = teaserPhrases[teaserIndex % Math.max(teaserPhrases.length, 1)] ?? "";

  // Close chat if user leaves Overview while Sarah is open on the workspace.
  useEffect(() => {
    if (!open) return;
    if (isWorkspaceRoute && workspaceTab !== "overview") setOpen(false);
  }, [isWorkspaceRoute, workspaceTab, open]);

  // Alternate teaser copy while panel is closed.
  useEffect(() => {
    if (!surface || open || !teaserVisible) return;
    const id = window.setInterval(() => {
      setTeaserIndex((i) => i + 1);
    }, 4000);
    return () => window.clearInterval(id);
  }, [surface, open, teaserVisible]);

  // Soft re-show teaser a while after chat is minimized.
  useEffect(() => {
    if (!surface || open) return;
    if (teaserVisible) return;
    const id = window.setTimeout(() => setTeaserVisible(true), 12_000);
    return () => window.clearTimeout(id);
  }, [surface, open, teaserVisible]);

  const seedKey =
    surface === "collab" && resolved
      ? `collab:${resolved.situationId}:${resolved.status ?? ""}:${resolved.role}`
      : surface === "guest"
        ? `guest:${location.pathname}`
        : "none";

  useEffect(() => {
    if (!surface) return;
    setMessages([]);
    setInput("");
    setFooterTab("chat");
    setBranchChips(null);
  }, [seedKey, surface]);

  // Load root chips from DB whenever the panel opens or surface changes (admin can add chips anytime).
  useEffect(() => {
    if (!surface) return;
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        if (surface === "guest") {
          const res = await trpc.help.sarahFaq.listChips.query({ surface: "GUEST_HOME" });
          if (cancelled) return;
          const chips = (res.chips || []) as Chip[];
          setRootChips(
            chips.length ? chips : GUEST_HOME_CHIPS.map((c) => ({ id: c.id, labelEn: c.labelEn })),
          );
        } else if (resolved) {
          const res = await trpc.help.sarahFaq.listChips.query({
            surface: "COLLABORATION",
            situationId: resolved.situationId,
          });
          if (cancelled) return;
          const chips = (res.chips || []) as Chip[];
          setRootChips(
            chips.length
              ? chips
              : (resolved.chips || []).map((c) => ({ id: c.id, labelEn: c.labelEn })),
          );
        }
      } catch {
        if (cancelled) return;
        if (surface === "guest") {
          setRootChips(GUEST_HOME_CHIPS.map((c) => ({ id: c.id, labelEn: c.labelEn })));
        } else {
          setRootChips((resolved?.chips || []).map((c) => ({ id: c.id, labelEn: c.labelEn })));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [surface, seedKey, resolved?.situationId, open, footerTab]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading, open, footerTab]);

  const send = useCallback(
    async (raw: string, faqNodeId?: string) => {
      if (!surface) return;
      const message = raw.trim();
      if (!message || loading) return;

      setFooterTab("chat");
      setMessages((prev) => [
        ...prev,
        { id: `u-${Date.now()}`, role: "user", text: message, at: Date.now() },
      ]);
      setInput("");
      setLoading(true);
      try {
        const faqId = faqNodeId && isFaqChipId(faqNodeId) ? faqNodeId : undefined;
        const res =
          surface === "guest"
            ? await trpc.help.guestOnboardingChat.mutate({
                message,
                pathname: location.pathname,
                deviceId: getSarahDeviceId(),
                ...(faqId ? { faqNodeId: faqId } : {}),
              })
            : await trpc.help.collaborationChat.mutate({
                message,
                situationId: resolved!.situationId,
                role: resolved!.role,
                status: resolved!.status,
                workspaceLocked: resolved!.workspaceLocked,
                collaborationReadOnly: resolved!.collaborationReadOnly,
                userPaid: resolved!.userPaid,
                pathname: resolved!.pathname,
                ...(faqId ? { faqNodeId: faqId } : {}),
              });
        setMessages((prev) => [
          ...prev,
          { id: `a-${Date.now()}`, role: "assistant", text: res.reply, at: Date.now() },
        ]);
        const followUps = (res.suggestedFollowUps || []) as Chip[];
        if (res.source === "faq" && followUps.length) {
          setBranchChips(followUps);
        } else if (res.source === "faq") {
          // Leaf FAQ node — return to root topics
          setBranchChips(null);
        } else {
          setBranchChips(null);
        }
      } catch (err) {
        handleTrpcError(err);
        const limitMsg =
          surface === "guest"
            ? "You’ve reached Sarah’s free limit of 10 chats per 24 hours on this network or device. Please try again tomorrow, or Sign up for full access."
            : "You’ve reached Sarah’s limit of 10 chats per 24 hours on this account. Please try again tomorrow, or use Settings to contact support.";
        const isLimit = getTrpcErrorCode(err) === "TOO_MANY_REQUESTS";
        setMessages((prev) => [
          ...prev,
          {
            id: `e-${Date.now()}`,
            role: "assistant",
            text: isLimit
              ? limitMsg
              : surface === "guest"
                ? "I couldn’t answer right now. Please try again in a moment, or use Sign up / Log in in the header."
                : "I couldn’t answer right now. Please try again in a moment, or use Settings to contact support.",
            at: Date.now(),
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [surface, loading, location.pathname, resolved],
  );

  if (!surface) return null;

  const chips: Chip[] = branchChips?.length ? branchChips : rootChips;

  const footerTabs =
    surface === "guest"
      ? ([
          { id: "chat" as const, label: "Chat", icon: IconMessageCircle },
          { id: "topics" as const, label: "Topics", icon: IconLayoutGrid },
          { id: "help" as const, label: "Help", icon: IconHelpCircle },
        ] as const)
      : ([
          { id: "chat" as const, label: "Chat", icon: IconMessageCircle },
          { id: "topics" as const, label: "Topics", icon: IconLayoutGrid },
          { id: "guide" as const, label: "Guide", icon: IconBook2 },
          { id: "help" as const, label: "Help", icon: IconHelpCircle },
        ] as const);

  return (
    <>
      <Transition mounted={open} transition="slide-up" duration={200} timingFunction="ease">
        {(styles) => (
          <Paper
            style={{
              ...styles,
              position: "fixed",
              right: SARAH_FAB_RIGHT,
              bottom: panelBottom,
              width: "min(380px, calc(100vw - 24px))",
              height: panelMaxHeight,
              maxHeight: panelMaxHeight,
              zIndex: 400,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderRadius: 18,
              boxShadow: "0 18px 50px rgba(4, 48, 92, 0.28)",
              border: "1px solid #E2E8F0",
            }}
          >
            <Box
              px="md"
              py="sm"
              style={{
                flexShrink: 0,
                background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_SOFT} 55%, #0d5fa3 100%)`,
                color: "#fff",
              }}
            >
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                  <HelpBotAvatar size={48} />
                  <Box style={{ minWidth: 0 }}>
                    <Text fw={700} size="sm" c="#fff" truncate>
                      Sarah
                    </Text>
                    <Group gap={6} mt={2}>
                      <Box
                        w={8}
                        h={8}
                        style={{ borderRadius: "50%", background: "#22C55E", flexShrink: 0 }}
                      />
                      <Text size="xs" c="rgba(255,255,255,0.9)">
                        AI Assistant · Online
                      </Text>
                    </Group>
                    <Text size="xs" c="rgba(255,255,255,0.75)" mt={2} lineClamp={1}>
                      Your Smart Guide for GTsetu
                    </Text>
                  </Box>
                </Group>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  c="#fff"
                  aria-label="Minimize"
                  onClick={() => setOpen(false)}
                >
                  <IconMinus size={18} />
                </ActionIcon>
              </Group>
            </Box>

            <Box
              style={{
                flex: 1,
                minHeight: 0,
                background: "#F8FAFC",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {footerTab === "chat" || footerTab === "topics" ? (
                <>
                  <ScrollArea
                    style={{ flex: 1 }}
                    px="md"
                    pt="md"
                    viewportRef={scrollRef}
                    type="auto"
                    offsetScrollbars
                  >
                    <Stack gap="sm" pb="sm">
                      {footerTab === "chat"
                        ? messages.map((m) => {
                            const isUser = m.role === "user";
                            return (
                              <Flex
                                key={m.id}
                                gap={6}
                                justify={isUser ? "flex-end" : "flex-start"}
                                align="flex-end"
                              >
                                {!isUser ? <HelpBotAvatar size={22} /> : null}
                                <Box
                                  style={{
                                    maxWidth: "84%",
                                    background: isUser ? NAVY : "#FFFFFF",
                                    borderRadius: 10,
                                    padding: "6px 9px",
                                    border: isUser ? "none" : "1px solid #E2E8F0",
                                    boxShadow: isUser ? "none" : "0 1px 2px rgba(15,23,42,0.04)",
                                  }}
                                >
                                  {isUser ? (
                                    <Text
                                      size="xs"
                                      c="#FFFFFF"
                                      fw={400}
                                      lh={1.4}
                                      style={{ whiteSpace: "pre-wrap", fontSize: 12 }}
                                    >
                                      {m.text}
                                    </Text>
                                  ) : (
                                    <SarahHelpMarkdown content={m.text} color="#0F172A" size="xs" />
                                  )}
                                  <Text
                                    size="xs"
                                    mt={4}
                                    ta="right"
                                    lh={1.2}
                                    style={{ fontSize: 10 }}
                                    c={isUser ? "rgba(255,255,255,0.65)" : "dimmed"}
                                  >
                                    {formatTime(m.at)}
                                  </Text>
                                </Box>
                              </Flex>
                            );
                          })
                        : null}

                      {(footerTab === "topics" ||
                        (footerTab === "chat" && messages.length === 0 && !loading) ||
                        (footerTab === "chat" && Boolean(branchChips?.length) && !loading)) && (
                        <Stack gap={6}>
                          {footerTab === "topics" ? (
                            <Text size="xs" c="dimmed" px={2} style={{ fontSize: 11 }}>
                              Suggested questions for this page
                            </Text>
                          ) : branchChips?.length && messages.length > 0 ? (
                            <Text size="xs" c="dimmed" px={2} style={{ fontSize: 11 }}>
                              Related questions
                            </Text>
                          ) : null}
                          {branchChips?.length ? (
                            <UnstyledButton
                              onClick={() => setBranchChips(null)}
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: "#0369A1",
                                padding: "2px 4px",
                                alignSelf: "flex-start",
                              }}
                            >
                              ← Back to topics
                            </UnstyledButton>
                          ) : null}
                          {chips.map((chip, i) => (
                            <UnstyledButton
                              key={chip.id}
                              onClick={() => void send(chip.labelEn, chip.id)}
                              disabled={loading}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                width: "100%",
                                background: "#fff",
                                border: "1px solid #E2E8F0",
                                borderRadius: 8,
                                padding: "6px 8px",
                                textAlign: "left",
                                minHeight: 32,
                              }}
                            >
                              <ThemeIcon
                                size={20}
                                radius="sm"
                                variant="light"
                                style={{
                                  background: `${SUGGESTION_ACCENTS[i % SUGGESTION_ACCENTS.length]}18`,
                                  color: SUGGESTION_ACCENTS[i % SUGGESTION_ACCENTS.length],
                                  flexShrink: 0,
                                }}
                              >
                                <IconCircleDot size={11} />
                              </ThemeIcon>
                              <Text
                                size="xs"
                                fw={500}
                                c="#0F172A"
                                lh={1.3}
                                style={{ flex: 1, minWidth: 0, fontSize: 12 }}
                              >
                                {chip.labelEn}
                              </Text>
                              <IconChevronRight size={14} color="#94A3B8" style={{ flexShrink: 0 }} />
                            </UnstyledButton>
                          ))}
                        </Stack>
                      )}

                      {loading && footerTab === "chat" ? (
                        <Group gap="xs" px={4}>
                          <Loader size="xs" color={NAVY} />
                          <Text size="xs" c="dimmed">
                            Thinking…
                          </Text>
                        </Group>
                      ) : null}
                    </Stack>
                  </ScrollArea>

                  {footerTab === "chat" ? (
                    <Box px="md" pb="sm" pt={4} style={{ flexShrink: 0 }}>
                      <Box
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          background: "#fff",
                          border: "1px solid #E2E8F0",
                          borderRadius: 999,
                          padding: "6px 8px 6px 14px",
                        }}
                      >
                        <TextInput
                          variant="unstyled"
                          placeholder="Ask me anything…"
                          value={input}
                          onChange={(e) => setInput(e.currentTarget.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              void send(input);
                            }
                          }}
                          disabled={loading}
                          style={{ flex: 1 }}
                          styles={{ input: { fontSize: 14 } }}
                        />
                        <ActionIcon
                          radius="xl"
                          size={36}
                          variant="filled"
                          style={{ background: NAVY }}
                          onClick={() => void send(input)}
                          disabled={loading || !input.trim()}
                          aria-label="Send"
                        >
                          <IconSend size={16} />
                        </ActionIcon>
                      </Box>
                    </Box>
                  ) : null}
                </>
              ) : null}

              {surface === "collab" && footerTab === "guide" ? (
                <Stack gap="sm" p="md" style={{ overflow: "auto" }}>
                  <Text size="sm" fw={600} c="#0F172A">
                    Platform guide
                  </Text>
                  <Text size="sm" c="dimmed">
                    Open a short walkthrough of how to use GTsetu pages (profile, explore, collaborations, and
                    more).
                  </Text>
                  <UnstyledButton
                    onClick={() => {
                      setTourStepIndex(appUsageGuideStepIndexForPath(location.pathname));
                      setTourOpened(true);
                    }}
                    style={{
                      background: NAVY,
                      color: "#fff",
                      borderRadius: 12,
                      padding: "12px 14px",
                      fontWeight: 600,
                      fontSize: 14,
                      textAlign: "center",
                    }}
                  >
                    Open usage tour
                  </UnstyledButton>
                </Stack>
              ) : null}

              {footerTab === "help" ? (
                <Stack gap="sm" p="md" style={{ overflow: "auto" }}>
                  <Text size="sm" fw={600} c="#0F172A">
                    Need more help?
                  </Text>
                  {surface === "guest" ? (
                    <>
                      <Text size="sm" c="dimmed">
                        I explain GTsetu for visitors: what the platform is, how companies onboard, B2B
                        collaboration, Experts, and Corridors. I cannot create accounts for you.
                      </Text>
                      <Text size="sm" c="dimmed">
                        Ready to join? Use{" "}
                        <Text span fw={600} c={NAVY}>
                          Sign up
                        </Text>{" "}
                        or{" "}
                        <Text span fw={600} c={NAVY}>
                          Log in
                        </Text>{" "}
                        in the header, or contact GTsetu support.
                      </Text>
                    </>
                  ) : (
                    <>
                      <Text size="sm" c="dimmed">
                        I only explain Collaboration Workspace steps. For account or billing issues, open{" "}
                        <Text span fw={600} c={NAVY}>
                          Settings
                        </Text>{" "}
                        or contact GTsetu support.
                      </Text>
                      <Text size="xs" c="dimmed">
                        Current step: {(resolved?.situationId ?? "").replace(/_/g, " ")}
                      </Text>
                    </>
                  )}
                </Stack>
              ) : null}
            </Box>

            <Box
              style={{
                flexShrink: 0,
                borderTop: "1px solid #E2E8F0",
                background: "#fff",
                display: "grid",
                gridTemplateColumns: `repeat(${footerTabs.length}, 1fr)`,
                padding: "6px 4px 8px",
              }}
            >
              {footerTabs.map((tab) => {
                const active = footerTab === tab.id;
                const Icon = tab.icon;
                return (
                  <UnstyledButton
                    key={tab.id}
                    onClick={() => setFooterTab(tab.id)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                      padding: "6px 2px",
                      borderRadius: 8,
                    }}
                  >
                    <Icon size={18} color={active ? NAVY : "#94A3B8"} />
                    <Text size="xs" fw={active ? 700 : 500} c={active ? NAVY : "dimmed"}>
                      {tab.label}
                    </Text>
                  </UnstyledButton>
                );
              })}
            </Box>
          </Paper>
        )}
      </Transition>

      <Box
        style={{
          position: "fixed",
          right: SARAH_FAB_RIGHT,
          bottom: fabBottom,
          zIndex: 401,
          width: SARAH_FAB_SIZE,
          height: SARAH_FAB_SIZE,
          // Room for speech bubble above FAB without clipping
          overflow: "visible",
        }}
      >
        {!open ? (
          <SarahTeaserBubble
            key={teaserIndex}
            text={teaserText}
            visible={teaserVisible && Boolean(teaserText)}
            onClick={() => {
              setOpen(true);
              setTeaserVisible(false);
            }}
          />
        ) : null}

        <UnstyledButton
          type="button"
          aria-label={open ? "Close Sarah" : "Open Sarah, GTsetu AI Assistant"}
          onClick={() => {
            setOpen((v) => {
              const next = !v;
              if (next) setTeaserVisible(false);
              return next;
            });
          }}
          style={{
            position: "relative",
            padding: 0,
            margin: 0,
            border: "none",
            background: "transparent",
            lineHeight: 0,
            boxShadow: "none",
            borderRadius: "50%",
            overflow: "hidden",
            animation: open ? "none" : "sarahFabFloat 2.8s ease-in-out infinite",
          }}
        >
          <HelpBotAvatar size={SARAH_FAB_SIZE} />
        </UnstyledButton>

        {/* Local keyframes for gentle float */}
        <style>{`
          @keyframes sarahFabFloat {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-5px); }
          }
        `}</style>
      </Box>

      {surface === "collab" ? (
        <PortalUsageGuideModal
          opened={tourOpened}
          onClose={() => setTourOpened(false)}
          stepIndex={tourStepIndex}
          onStepChange={setTourStepIndex}
          steps={APP_USAGE_GUIDE_STEPS}
          stepIndexForPath={appUsageGuideStepIndexForPath}
        />
      ) : null}
    </>
  );
}
